export enum ProjectCategoryAttachment {
	ELIGIBILITIES = "eligibilities",
	NOA = "noa",
	NTP = "ntp",
	PO = "po",
	DELIVERY = "delivery",
	DV = "dv",
	CONTRACT = "contract",
	PROPOSAL = "proposal",
	PERFORMANCE_BOND = "performance_bond",
}

export interface ProjectCategoryAttachmentInfo {
	value: string;
	label: string;
	description: string;
	icon: string;
	color: string;
}

export const PROJECT_CATEGORY_ATTACHMENT_INFO: Record<ProjectCategoryAttachment, ProjectCategoryAttachmentInfo> = {
	[ProjectCategoryAttachment.ELIGIBILITIES]: {
		value: "eligibilities",
		label: "Eligibilities",
		description: "Documents related to project eligibility requirements",
		icon: "IconClipboardCheck",
		color: "blue",
	},
	[ProjectCategoryAttachment.NOA]: {
		value: "noa",
		label: "Notice of Award (NOA)",
		description: "Official notice of award documentation",
		icon: "IconAward",
		color: "green",
	},
	[ProjectCategoryAttachment.NTP]: {
		value: "ntp",
		label: "Notice to Proceed (NTP)",
		description: "Notice to proceed with project implementation",
		icon: "IconPlayerPlay",
		color: "purple",
	},
	[ProjectCategoryAttachment.PO]: {
		value: "po",
		label: "Purchase Order (PO)",
		description: "Purchase order and procurement documents",
		icon: "IconShoppingCart",
		color: "orange",
	},
	[ProjectCategoryAttachment.DELIVERY]: {
		value: "delivery",
		label: "Delivery",
		description: "Delivery receipts and related documentation",
		icon: "IconTruck",
		color: "teal",
	},
	[ProjectCategoryAttachment.DV]: {
		value: "dv",
		label: "Disbursement Voucher (DV)",
		description: "Disbursement voucher and payment documentation",
		icon: "IconReceipt",
		color: "yellow",
	},
	[ProjectCategoryAttachment.CONTRACT]: {
		value: "contract",
		label: "Contract",
		description: "Contract agreements and related legal documents",
		icon: "IconFileText",
		color: "red",
	},
	[ProjectCategoryAttachment.PROPOSAL]: {
		value: "proposal",
		label: "Proposal",
		description: "Proposal documents and submissions",
		icon: "IconFileDescription",
		color: "indigo",
	},
	[ProjectCategoryAttachment.PERFORMANCE_BOND]: {
		value: "performance_bond",
		label: "Performance Bond",
		description: "Performance bond and surety documentation",
		icon: "IconShieldCheck",
		color: "cyan",
	},
};

/**
 * Get all attachment types as an array
 */
export const getAllProjectCategoryAttachments = (): ProjectCategoryAttachmentInfo[] => {
	return Object.values(PROJECT_CATEGORY_ATTACHMENT_INFO);
};

/**
 * Get attachment info by value
 */
export const getProjectCategoryAttachmentInfo = (value: string): ProjectCategoryAttachmentInfo | undefined => {
	return PROJECT_CATEGORY_ATTACHMENT_INFO[value as ProjectCategoryAttachment];
};

/**
 * Get only the values as an array
 */
export const getProjectCategoryAttachmentValues = (): string[] => {
	return Object.values(ProjectCategoryAttachment);
};

/**
 * Get only the labels as an array
 */
export const getProjectCategoryAttachmentLabels = (): string[] => {
	return Object.values(PROJECT_CATEGORY_ATTACHMENT_INFO).map((info) => info.label);
};

/**
 * Check if a value is valid
 */
export const isValidProjectCategoryAttachment = (value: string): boolean => {
	return Object.values(ProjectCategoryAttachment).includes(value as ProjectCategoryAttachment);
};

/**
 * Get attachment types commonly used for government projects
 */
export const getGovernmentProjectAttachments = (): ProjectCategoryAttachmentInfo[] => {
	const governmentTypes = [
		ProjectCategoryAttachment.ELIGIBILITIES,
		ProjectCategoryAttachment.NOA,
		ProjectCategoryAttachment.NTP,
		ProjectCategoryAttachment.CONTRACT,
		ProjectCategoryAttachment.DV,
	];
	return governmentTypes.map((type) => PROJECT_CATEGORY_ATTACHMENT_INFO[type]);
};

/**
 * Get attachment types commonly used for private projects
 */
export const getPrivateProjectAttachments = (): ProjectCategoryAttachmentInfo[] => {
	const privateTypes = [ProjectCategoryAttachment.PO, ProjectCategoryAttachment.DELIVERY, ProjectCategoryAttachment.CONTRACT];
	return privateTypes.map((type) => PROJECT_CATEGORY_ATTACHMENT_INFO[type]);
};

/**
 * Get color classes for Tailwind CSS
 */
export const getAttachmentColorClasses = (color: string) => ({
	bg: `bg-${color}-100`,
	text: `text-${color}-800`,
	border: `border-${color}-200`,
	hover: `hover:bg-${color}-200`,
});
